import * as React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
export default function ConditionsWeTreat() {
  const conditions = [{
    id: 1,
    title: "Back Pain",
    overview: "Lower back pain is one of the most common reasons people seek physiotherapy treatment.",
    symptoms: "Pain in the lower or upper back, stiffness, difficulty bending or standing, radiating pain down the legs.",
    treatment: "Manual therapy, core strengthening exercises, postural correction, pain management strategies.",
    approach: "At Align Physiotherapy, we use evidence-based assessment to identify the root cause of your back pain and create a personalized treatment plan to restore movement and reduce discomfort."
  }, {
    id: 2,
    title: "\xA0Osteoarthritis",
    overview: "Osteoarthritis involves the gradual wearing down of cartilage in the knee joint.",
    symptoms: "Joint pain, swelling, stiffness (especially in the morning), reduced range of motion, difficulty walking or climbing stairs.",
    treatment: "Strength training, joint mobilization, activity modification, education on self-management.",
    approach: "We focus on improving knee function and reducing pain through targeted exercises and hands-on therapy, helping you stay active and mobile."
  }, {
    id: 3,
    title: "Shoulder Pain",
    overview: "Shoulder pain can result from injuries, overuse, or underlying conditions affecting the rotator cuff and surrounding structures.",
    symptoms: "Pain with overhead movements, limited range of motion, weakness, discomfort when sleeping on the affected side.",
    treatment: "Rotator cuff strengthening, manual therapy, postural correction, range-of-motion exercises.",
    approach: "Our expert physiotherapists assess shoulder biomechanics and develop a tailored program to restore strength, flexibility, and pain-free movement."
  }, {
    id: 4,
    title: "Sports Injuries",
    overview: "Sports injuries include sprains, strains, tendonitis, and other acute or overuse injuries affecting athletes.",
    symptoms: "Sudden pain during activity, swelling, bruising, reduced performance, instability in joints.",
    treatment: "Injury-specific rehabilitation, strengthening and conditioning programs, return-to-sport planning, injury prevention strategies.",
    approach: "At Align Physiotherapy, we help athletes recover safely and efficiently, ensuring a strong return to their sport with reduced risk of re-injury."
  }, {
    id: 5,
    title: "Neck Pain",
    overview: "Neck pain can stem from poor posture, muscle strain, or underlying cervical spine conditions.",
    symptoms: "Stiffness, sharp or dull pain in the neck, headaches, reduced mobility, radiating pain into shoulders or arms.",
    treatment: "Manual therapy, postural retraining, strengthening exercises, ergonomic advice.",
    approach: "We provide comprehensive assessments to identify contributing factors and deliver effective, individualized treatment to relieve neck pain."
  }, {
    id: 6,
    title: "Hip Pain",
    overview: "Hip pain can result from arthritis, bursitis, muscle imbalances, or referred pain from the lower back.",
    symptoms: "Groin or outer hip pain, stiffness, difficulty walking or weight-bearing, reduced range of motion.",
    treatment: "Hip strengthening, manual therapy, gait retraining, activity modification.",
    approach: "Our team uses targeted exercises and manual techniques to improve hip function and reduce pain, helping you move more freely."
  }];
  return <section className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center animate-fade-in-up">
          <h2 className="font-display text-4xl font-bold tracking-wide text-foreground md:text-5xl">
            Conditions We Treat
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-base leading-relaxed">At Align Physiotherapy, we specialise in treating a wide range of musculoskeletal conditions with evidence-based, personalised care.</p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {conditions.map((condition, i) => <Card key={condition.id} data-index={i} className="border-border bg-background shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-medium animate-fade-in-up-delay-100">
              <CardHeader>
                <CardTitle className="font-display text-xl font-semibold text-foreground">
                  {condition.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                  {condition.overview}
                </p>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="symptoms">
                    <AccordionTrigger className="text-foreground text-sm font-medium">
                      Symptoms
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                      {condition.symptoms}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="treatment">
                    <AccordionTrigger className="text-foreground text-sm font-medium">
                      Treatment Options
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                      {condition.treatment}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="approach">
                    <AccordionTrigger className="text-foreground text-sm font-medium">
                      Our Approach
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                      {condition.approach}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 text-center animate-fade-in-up-delay-200">
          <p className="text-muted-foreground mb-6 text-base">
            Don't see your condition listed? We treat many other
            musculoskeletal issues.
          </p>
          <Link to="/ContactPage">
            <Button size="lg" className="group bg-primary text-primary-foreground font-medium shadow-md transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0">
              Contact Us
              <ChevronRight className="ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}